import { CJT_SUPPORT_WIDGET_CONFIG } from '@/config/cjtSupportWidget';

const SCRIPT_ID = 'cjt-support-widget-script';
const CUSTOMER_STORAGE_KEYS = ['objInfo', 'userInfo'];
const AUTH_STORAGE_KEYS = ['token', 'sessionId'];
const CUSTOMER_RELATED_STORAGE_KEYS = CUSTOMER_STORAGE_KEYS.concat(AUTH_STORAGE_KEYS);
const CUSTOMER_CHANGE_EVENT_NAME = 'cjt-support-widget-customer-change';
const OPEN_EVENT_NAME = 'cjt-support-widget-open';

let loadingPromise = null;
let initPromise = null;
let initialized = false;
let storagePatched = false;
let stopCustomerSync = null;
let lastInitializedCustomerSignature = '';
let verifiedCustomerAuthSignature = '';
let openRequested = false;
let lifecycleGeneration = 0;

function getSupportWidgetAgentCredentials() {
  if (!isCurrentCustomerSessionVerified()) return null;
  const token = normalizeCustomerValue(localStorage.getItem('token'));
  const sessionId = normalizeCustomerValue(localStorage.getItem('sessionId'));
  const userInfo = getStoredUserInfo();
  const mobile = getFirstCustomerValue([userInfo && userInfo.userMobile, userInfo && userInfo.mobile]).trim();

  return token && sessionId ? { token, sessionId, ...(mobile ? { mobile } : {}) } : null;
}

function normalizeCustomerValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
}

function getFirstCustomerValue(values) {
  for (let i = 0; i < values.length; i += 1) {
    const normalizedValue = normalizeCustomerValue(values[i]);

    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return '';
}

function safeParseJSON(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
}

function getEmptyCustomer() {
  return {
    customerDisplayName: '',
    customerId: '',
  };
}

function getCurrentCustomerAuthSignature() {
  const token = normalizeCustomerValue(localStorage.getItem('token'));
  const sessionId = normalizeCustomerValue(localStorage.getItem('sessionId'));

  if (!token || !sessionId) {
    return '';
  }

  return `${token}\n${sessionId}`;
}

function isCurrentCustomerSessionVerified() {
  const currentAuthSignature = getCurrentCustomerAuthSignature();

  return Boolean(currentAuthSignature && currentAuthSignature === verifiedCustomerAuthSignature);
}

function getStoredUserInfo() {
  return safeParseJSON(localStorage.getItem('objInfo') || '') || safeParseJSON(localStorage.getItem('userInfo') || '');
}

export function getSupportWidgetCustomer() {
  if (!isCurrentCustomerSessionVerified()) {
    return getEmptyCustomer();
  }

  const userInfo = getStoredUserInfo();
  const customerId = normalizeCustomerValue(userInfo && userInfo.userId);

  if (!customerId) {
    return getEmptyCustomer();
  }

  return {
    customerDisplayName: getFirstCustomerValue([
      userInfo.companyName,
      userInfo.nickname,
      userInfo.nickName,
      userInfo.userName,
      userInfo.mobile,
      userInfo.userMobile,
    ]),
    customerId,
  };
}

function getCustomerSignature(customer) {
  const credentials = getSupportWidgetAgentCredentials();
  return `${customer.customerId}\n${customer.customerDisplayName}\n${
    credentials ? `${credentials.token}\n${credentials.sessionId}\n${credentials.mobile || ''}` : ''
  }`;
}

function setScriptCustomerAttributes(script, customer) {
  if (!script) {
    return;
  }

  script.setAttribute('data-customer-display-name', customer.customerDisplayName);
  script.setAttribute('data-customer-id', customer.customerId);
}

function getInitOptions(customer) {
  return {
    siteKey: CJT_SUPPORT_WIDGET_CONFIG.siteKey,
    clientType: 'IIPTO',
    frameUrl: CJT_SUPPORT_WIDGET_CONFIG.frameUrl,
    language: CJT_SUPPORT_WIDGET_CONFIG.language,
    launcherElementId: CJT_SUPPORT_WIDGET_CONFIG.launcherElementId,
    position: CJT_SUPPORT_WIDGET_CONFIG.position,
    customerDisplayName: customer.customerDisplayName,
    customerId: customer.customerId,
    agentCredentials: getSupportWidgetAgentCredentials(),
  };
}

function isCustomerRelatedStorageKey(key) {
  return CUSTOMER_RELATED_STORAGE_KEYS.indexOf(String(key)) > -1;
}

function notifyCustomerChange(key) {
  if (isCustomerRelatedStorageKey(key)) {
    if (AUTH_STORAGE_KEYS.indexOf(String(key)) > -1) {
      verifiedCustomerAuthSignature = '';
    }
    window.dispatchEvent(new Event(CUSTOMER_CHANGE_EVENT_NAME));
  }
}

export function confirmCJTSupportWidgetCustomer() {
  verifiedCustomerAuthSignature = getCurrentCustomerAuthSignature();
  notifyCustomerChange('objInfo');
}

export function clearCJTSupportWidgetCustomerVerification() {
  verifiedCustomerAuthSignature = '';
  notifyCustomerChange('objInfo');
}

function ensureLocalStorageCustomerEvent() {
  if (storagePatched) {
    return;
  }

  const storagePrototype = Object.getPrototypeOf(localStorage);
  const originalSetItem = storagePrototype.setItem;
  const originalRemoveItem = storagePrototype.removeItem;
  const originalClear = storagePrototype.clear;

  try {
    storagePrototype.setItem = function setItem(key) {
      const previousValue = this.getItem(key);
      const result = originalSetItem.apply(this, arguments);
      if (this === localStorage && previousValue !== this.getItem(key)) {
        notifyCustomerChange(key);
      }
      return result;
    };

    storagePrototype.removeItem = function removeItem(key) {
      const previousValue = this.getItem(key);
      const result = originalRemoveItem.apply(this, arguments);
      if (this === localStorage && previousValue !== null) {
        notifyCustomerChange(key);
      }
      return result;
    };
    storagePrototype.clear = function clear() {
      const result = originalClear.apply(this, arguments);
      if (this === localStorage) {
        notifyCustomerChange('token');
      }
      return result;
    };
    storagePatched = true;
  } catch (error) {
    storagePatched = true;
  }
}

function loadScript() {
  if (window.CJTSupportWidget) {
    return Promise.resolve();
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise((resolve, reject) => {
    const customer = getSupportWidgetCustomer();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = CJT_SUPPORT_WIDGET_CONFIG.scriptUrl;
    script.async = true;
    script.setAttribute('data-auto-init', 'false');
    setScriptCustomerAttributes(script, customer);
    script.onload = resolve;
    script.onerror = (error) => {
      loadingPromise = null;
      script.parentNode.removeChild(script);
      reject(error);
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
}

export function initCJTSupportWidget() {
  if (initialized) {
    return syncCJTSupportWidgetCustomerData();
  }

  if (initPromise) {
    return initPromise;
  }

  const generation = lifecycleGeneration;
  initPromise = loadScript()
    .then(() => {
      if (generation !== lifecycleGeneration) return;
      if (!window.CJTSupportWidget || typeof window.CJTSupportWidget.init !== 'function') {
        throw new Error('CJT Support Widget init API is unavailable.');
      }

      initialized = true;
      return syncCJTSupportWidgetCustomerData();
    })
    .catch((error) => {
      initPromise = null;
      throw error;
    });

  return initPromise;
}

export function openCJTSupportWidget() {
  const generation = lifecycleGeneration;
  return initCJTSupportWidget().then(() => {
    if (generation !== lifecycleGeneration) return;
    if (!lastInitializedCustomerSignature) {
      openRequested = true;
      return;
    }
    if (!window.CJTSupportWidget || typeof window.CJTSupportWidget.open !== 'function') {
      throw new Error('CJT Support Widget open API is unavailable.');
    }

    window.CJTSupportWidget.open();
  });
}

export function syncCJTSupportWidgetCustomerData() {
  if (!initialized) {
    return Promise.resolve();
  }

  const customer = getSupportWidgetCustomer();
  const hasAuth = Boolean(localStorage.getItem('token') || localStorage.getItem('sessionId'));
  if (hasAuth && (!isCurrentCustomerSessionVerified() || !customer.customerId)) {
    if (window.CJTSupportWidget && typeof window.CJTSupportWidget.destroy === 'function') {
      window.CJTSupportWidget.destroy();
    }
    lastInitializedCustomerSignature = '';
    return Promise.resolve();
  }
  const customerSignature = getCustomerSignature(customer);

  setScriptCustomerAttributes(document.getElementById(SCRIPT_ID), customer);

  if (customerSignature === lastInitializedCustomerSignature) {
    return Promise.resolve();
  }

  if (!window.CJTSupportWidget || typeof window.CJTSupportWidget.init !== 'function') {
    return Promise.resolve();
  }

  if (typeof window.CJTSupportWidget.destroy === 'function') {
    window.CJTSupportWidget.destroy();
  }

  window.CJTSupportWidget.init(getInitOptions(customer));
  lastInitializedCustomerSignature = customerSignature;
  initialized = true;
  if (openRequested && typeof window.CJTSupportWidget.open === 'function') {
    openRequested = false;
    window.CJTSupportWidget.open();
  }

  return Promise.resolve();
}

export function startCJTSupportWidgetCustomerSync() {
  if (stopCustomerSync) {
    return stopCustomerSync;
  }

  ensureLocalStorageCustomerEvent();

  const handleCustomerChange = () => {
    syncCJTSupportWidgetCustomerData().catch((error) => {
      console.warn('[CJTSupportWidget] customer sync failed:', error);
    });
  };
  const handleStorage = (event) => {
    if (!event.key || isCustomerRelatedStorageKey(event.key)) {
      if (!event.key || AUTH_STORAGE_KEYS.indexOf(event.key) > -1) {
        verifiedCustomerAuthSignature = '';
      }
      handleCustomerChange();
    }
  };
  const handleOpenRequest = () => {
    openCJTSupportWidget().catch((error) => {
      console.warn('[CJTSupportWidget] open failed:', error);
    });
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(CUSTOMER_CHANGE_EVENT_NAME, handleCustomerChange);
  window.addEventListener(OPEN_EVENT_NAME, handleOpenRequest);

  stopCustomerSync = function stop() {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(CUSTOMER_CHANGE_EVENT_NAME, handleCustomerChange);
    window.removeEventListener(OPEN_EVENT_NAME, handleOpenRequest);
    stopCustomerSync = null;
  };

  return stopCustomerSync;
}

export function destroyCJTSupportWidget() {
  lifecycleGeneration += 1;
  openRequested = false;
  if (window.CJTSupportWidget && typeof window.CJTSupportWidget.destroy === 'function') {
    window.CJTSupportWidget.destroy();
  }
  initPromise = null;
  initialized = false;
  lastInitializedCustomerSignature = '';
}



// WEBPACK FOOTER //
// ./src/utils/cjtSupportWidget.js